import React, { useState, useEffect, useRef, useCallback, type ReactNode } from 'react';

import { LeftOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import { Select, Button, Space, Row, Col, Typography, Tooltip, Result } from 'antd';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { formatDateTime } from '../../../utils/dateTime';

import { useRecordContext } from './Context';
import Loader from './Loader';
import SelectAll from './SelectAll';
import SubHeader from './SubHeader';

import type { RecordId, TableAction } from './TableIndex';

const { Text } = Typography;

// 模組常量：避免每次 render 產生新陣列 ref，導致 RDT 把 pagination 子樹當成不同 component 整段重建
// （高頻 re-render 時 button 會被 unmount 重 mount → CSS transition / hover state 一直 reset → 視覺閃爍且難點擊）。
const PAGE_SIZE_OPTIONS = [
  { label: '10', value: 10 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];
const PAGINATION_ROWS_PER_PAGE_OPTIONS = [ 10, 20, 30, 50, 100, 200 ];

interface CellWithTooltipProps {
  content: ReactNode;
}

// 通用的 Cell Wrapper 組件，自動為被截斷的內容加上 Tooltip
const CellWithTooltip: React.FC<CellWithTooltipProps> = ({ content }) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (element) {
      // 檢查內容是否被截斷
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [content]);

  // 如果內容是 null/undefined，顯示 '-'
  if (content === null || content === undefined) {
    return <span>-</span>;
  }

  // 如果內容是空字串或 '-'，直接顯示
  if (content === '' || content === '-') {
    return <span>{content}</span>;
  }

  // 如果是 React 元素（如 Link、Tag 等），直接返回不處理
  if (React.isValidElement(content)) {
    return content;
  }

  // 轉換為字串（處理數字、布林值等）
  const displayContent = String(content);

  return (
    <Tooltip title={isTruncated ? displayContent : null}>
      <div
        ref={textRef}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: isTruncated ? 'help' : 'default',
        }}
      >
        {displayContent}
      </div>
    </Tooltip>
  );
};

interface ErrorResultProps {
  t: TFunction;
  onClear: () => void;
}

// Error Result component
const ErrorResult: React.FC<ErrorResultProps> = ({ t, onClear }) => (
  <div style={{ padding: '48px 0', background: '#fff' }}>
    <Result
      status="error"
      icon={<WarningOutlined />}
      title={t('common.table.load_error') as string}
      subTitle={t('common.table.load_error_retry') as string}
      extra={
        <Button type="primary" danger onClick={onClear}>
          {t('common.table.clear_and_retry') as string}
        </Button>
      }
    />
  </div>
);

// Column shape 與 react-data-table-component 對齊；外加自訂欄位（nameKey, sortFieldName, sticky, persist, visible 等）
interface TableColumn {
  name?: ReactNode;
  nameKey?: string;
  sortFieldName?: string;
  sticky?: 'right' | string;
  persist?: boolean;
  visible?: boolean;
  cell?: (row: any) => ReactNode;
  selector?: string | ((row: any) => unknown);
  disableTooltip?: boolean;
  id?: string;
  [key: string]: unknown;
}

interface BatchEditPanelArgs {
  selected: Set<RecordId>;
  showPanel?: boolean;
  onClose?: () => void;
  onSubmit: () => void;
  query: Record<string, unknown>;
  setLastModified: (ts: number) => void;
}

interface BatchEditPanel {
  render: (args: BatchEditPanelArgs) => ReactNode;
}

interface TableProps {
  onHeaderColumnsChange?: (columns: TableColumn[]) => void;
  columns: TableColumn[];
  columnsEditable?: boolean;
  tableId: string;
  actions?: TableAction[];
  showPanel?: boolean;
  onClosePanel?: () => void;
  batchEditPanel?: BatchEditPanel;
  testIdPrefix?: string;
  expandableRows?: boolean;
  expandableRowsComponent?: React.ComponentType<any>;
  // Dead pass-through：原 Table.jsx 沒 destructure 但 TableIndex 在傳，
  // 仍從 RecordContext 取，保留 prop 以維持 runtime 行為。
  queries?: Record<string, unknown>;
  setLastModified?: (value: number) => void;
}

interface RecordContextShape {
  records?: unknown[];
  selected?: Set<RecordId>;
  hasMore?: boolean;
  loading?: boolean;
  setLastModified?: (ts: number) => void;
  query?: Record<string, unknown>;
  onSelectRecord?: (id: RecordId) => void;
  onSelectAllRecords?: () => Promise<unknown>;
  onChangeSorting?: (...args: any[]) => void;
  onChangePage?: (page: number) => void;
  page?: number;
  onChangePageSize?: (size: number) => void;
  pageSize?: number;
  hasError?: boolean;
  clearAllSettings?: () => void;
}

const Table: React.FC<TableProps> = ({
  onHeaderColumnsChange,
  columns,
  columnsEditable,
  tableId,
  actions,
  showPanel,
  onClosePanel,
  batchEditPanel,  // 新增：批次編輯面板配置
  testIdPrefix,  // E2E 測試用的 data-testid 前綴
  expandableRows,  // 是否啟用展開列
  expandableRowsComponent,  // 展開列的渲染元件
}) => {
  // 當沒有 actions 時，自動隱藏 checkbox
  const shouldShowCheckbox = actions && actions.length > 0;
  const context = (useRecordContext() || {}) as RecordContextShape;
  const {
    records = [],
    selected = new Set<RecordId>(),
    hasMore = false,
    loading = false,
    setLastModified = () => { },
    query = {},
    onSelectRecord = () => { },
    onSelectAllRecords = () => Promise.resolve(),
    onChangeSorting = () => { },
    onChangePage = () => { },
    page = 1,
    onChangePageSize = () => { },
    pageSize = 10,
    hasError = false,
    clearAllSettings = () => { },
  } = context;
  const { t } = useTranslation();


  // 檢查是否有 sticky column，注入對應 CSS
  const hasStickyRight = columns.some((c) => c.sticky === 'right');
  useEffect(() => {
    if (!hasStickyRight) return;

    const styleId = 'rdt-sticky-right-style';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      [data-column-id="sticky-right"] {
        position: sticky !important;
        right: 0 !important;
        z-index: 2 !important;
        background-color: inherit !important;
      }
      .rdt_TableHeadRow > [data-column-id="sticky-right"] {
        z-index: 3 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [hasStickyRight]);

  // 自動處理欄位：格式化 datetime 並加上 Tooltip + sticky
  const processColumn = (column: TableColumn): TableColumn => {
    // 處理 sticky column：設定 id 讓 CSS attribute selector 生效
    if (column.sticky === 'right') {
      column = { ...column, id: 'sticky-right' };
    }
    const dateTimeFields = [
      'created_at', 'updated_at', 'shipped_at', 'delivered_at',
      'processed_at', 'received_at', 'verified_at', 'done_at',
      'arrived_at', 'putaway_at',
      'eta', 'scheduled_shipping_date', 'delivery_date', 'scheduled_date',
      'placed_at'
    ];

    // 檢查 selector 是否是 datetime 欄位（通過分析函數或字串）
    const isDateTimeField = (selector: TableColumn['selector']): boolean => {
      if (typeof selector === 'string') {
        return dateTimeFields.includes(selector);
      }
      if (typeof selector === 'function') {
        // 分析函數字串，查找 datetime 欄位名稱
        const funcString = selector.toString();
        return dateTimeFields.some((field) => {
          // 匹配 ({ field }) => field 或 (row) => row.field
          const patterns = [
            new RegExp(`\\{\\s*${field}\\s*\\}`),  // ({ field })
            new RegExp(`\\.${field}\\b`)            // row.field
          ];
          return patterns.some((pattern) => pattern.test(funcString));
        });
      }
      return false;
    };

    // 如果欄位已經有自訂 cell，且明確不需要 Tooltip（通過 disableTooltip: true）
    if (column.disableTooltip) {
      return column;
    }

    // 如果欄位已經有自訂 cell，包裝它加上 Tooltip
    if (column.cell) {
      const originalCell = column.cell;
      return {
        ...column,
        cell: (row: any) => {
          const content = originalCell(row);
          return <CellWithTooltip content={content} />;
        }
      };
    }

    // 如果是 datetime 欄位，自動格式化並加上 Tooltip
    if (isDateTimeField(column.selector)) {
      return {
        ...column,
        cell: (row: any) => {
          // 取得原始值
          const rawValue = typeof column.selector === 'function'
            ? column.selector(row)
            : row[column.selector as string];
          // 格式化 datetime
          const content = formatDateTime(rawValue as string);
          return <CellWithTooltip content={content as ReactNode} />;
        }
      };
    }

    // 如果有 selector 但沒有 cell，加上 Tooltip wrapper
    if (column.selector) {
      return {
        ...column,
        cell: (row: any) => {
          // selector 可能是函數或字串
          const content = typeof column.selector === 'function'
            ? column.selector(row)
            : row[column.selector as string];
          return <CellWithTooltip content={content as ReactNode} />;
        }
      };
    }

    return column;
  };

  const [headerColumns, setHeaderColumns] = useState<TableColumn[]>(
    columns.map((column) => {
      let processedColumn: TableColumn = { ...column, visible: true };
      // 只有當沒有自訂 name 時，才用 nameKey 翻譯
      if (column.nameKey && !column.name) {
        processedColumn.name = t(column.nameKey) as string;
      }
      // 自動處理欄位（datetime 格式化 + Tooltip）
      processedColumn = processColumn(processedColumn);
      return processedColumn;
    })
  );

  // Update headerColumns when columns prop changes (e.g., when language switches)
  useEffect(() => {
    setHeaderColumns((prevColumns) => {
      const newColumns = columns.map((column) => {
        // Handle i18n key translation
        let processedColumn: TableColumn = { ...column, visible: true };
        // 只有當沒有自訂 name 時，才用 nameKey 翻譯
        if (column.nameKey && !column.name) {
          processedColumn.name = t(column.nameKey) as string;
        }
        // 自動處理欄位（datetime 格式化 + Tooltip）
        processedColumn = processColumn(processedColumn);
        return processedColumn;
      });

      // Preserve visibility settings from previous state if columns exist
      if (prevColumns.length > 0) {
        return newColumns.map((newColumn) => {
          const existingColumn = prevColumns.find((prev) =>
            // 優先使用 nameKey 比較（字符串）
            (prev.nameKey && newColumn.nameKey && prev.nameKey === newColumn.nameKey) ||
            // 其次使用 sortFieldName 比較（字符串）
            (prev.sortFieldName && newColumn.sortFieldName && prev.sortFieldName === newColumn.sortFieldName) ||
            // 最後使用 name 比較（只有當兩者都是字符串時）
            (typeof prev.name === 'string' && typeof newColumn.name === 'string' && prev.name === newColumn.name)
          );
          return {
            ...newColumn,
            visible: existingColumn ? existingColumn.visible : true
          };
        });
      }

      return newColumns;
    });
  }, [columns, t]);

  const selectAllHeader: TableColumn = {
    name: <SelectAll />,
    width: '25px',
    sortable: false,
    compact: "true",
    persist: true,
    cell: ({ id }: { id: RecordId }) => (
      <label className='custom-control custom-checkbox' htmlFor={`SelectItem-${id}`}>
        <input
          id={`SelectItem-${id}`}
          className='custom-control-input'
          key={String(id)}
          type="checkbox"
          checked={selected.has(id)}
          onChange={() => onSelectRecord(id)}
        />
        <span className='custom-control-indicator'></span>
        &nbsp;
      </label>
    ),
    visible: true
  };

  const onColumnsChange = (cols: TableColumn[]) => {
    onHeaderColumnsChange && onHeaderColumnsChange(cols);
    setHeaderColumns(cols);
  };

  // RDT 把 paginationComponent prop 當 React component；ref 變動會 unmount/remount 整段 pagination 子樹，
  // 高頻 re-render（如 background job progress 通知洪流）時 buttons 反覆被砍掉重建→閃爍且點不下去。
  // 用 useCallback 鎖住 ref；只在實際分頁狀態變動時重建。
  const PaginationComponent = useCallback(() => (
    <Row justify="space-between" align="middle" style={{ padding: '16px 0' }}>
      <Col>
        <Space align="center">
          <Text>{t('common.table.page_size') as string}</Text>
          <Select
            value={pageSize}
            onChange={onChangePageSize}
            style={{ width: 80 }}
            options={PAGE_SIZE_OPTIONS}
          />
        </Space>
      </Col>
      <Col>
        <Space>
          <Button
            onClick={() => onChangePage(page - 1)}
            disabled={page <= 1}
            icon={<LeftOutlined />}
          >
            {t('common.table.previous') as string}
          </Button>
          <Button
            type="primary"
            onClick={() => onChangePage(page + 1)}
            disabled={!((hasMore && records.length === pageSize * page) || records.length > pageSize * page)}
            icon={<RightOutlined />}
            iconPosition="end"
          >
            {t('common.table.next') as string}
          </Button>
        </Space>
      </Col>
    </Row>
  ), [pageSize, page, hasMore, records.length, onChangePage, onChangePageSize, t]);

  return (
    <>
      <SubHeader
        selected={selected}
        onSelectAllRecords={onSelectAllRecords}
        actions={actions}
        headerColumns={headerColumns}
        onColumnsChange={onColumnsChange}
        columnsEditable={columnsEditable}
        tableId={tableId}
      />
      <div className="ibox-content dataTable-container" style={{ minHeight: '500px' }} {...(testIdPrefix && { 'data-testid': `${testIdPrefix}-table` })}>
        {hasError && !loading ? (
          <ErrorResult t={t} onClear={clearAllSettings} />
        ) : (
          <DataTable
            fixedHeader
            noHeader
            noDataComponent={
              <div style={{ padding: '24px', color: 'rgba(0, 0, 0, 0.45)' }}>
                {t('common.table.no_data') as string}
              </div>
            }
            customStyles={{
              cells: { style: { overflow: 'hidden', minWidth: 0 } },
              headCells: { style: { overflow: 'hidden', minWidth: 0 } },
            }}
            columns={
              (shouldShowCheckbox ? [selectAllHeader, ...headerColumns.filter((column) => column.persist || column.visible)] : headerColumns.filter((column) => column.persist || column.visible)) as any
            }
            data={Array.isArray(records) ? records.slice((page - 1) * pageSize, page * pageSize) : []}
            pagination
            paginationComponent={PaginationComponent}
            paginationServer
            progressPending={loading}
            progressComponent={<Loader />}
            subHeader={false}
            persistTableHead
            selectableRowsVisibleOnly // 全選時，調整為只選取當前頁面上的產品
            onSort={onChangeSorting}
            paginationRowsPerPageOptions={PAGINATION_ROWS_PER_PAGE_OPTIONS}
            expandableRows={!!expandableRows}
            expandableRowsComponent={expandableRowsComponent}
          />
        )}
      </div>
      {
        batchEditPanel && batchEditPanel.render && batchEditPanel.render({
          selected,
          showPanel,
          onClose: onClosePanel,
          onSubmit: () => {
            setLastModified(Date.now());
          },
          query,
          setLastModified
        })
      }
    </>
  );
};

export default Table;
